// @ts-check
import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import { isPlatform } from '@ionic/react';

import { useClassroomContext } from 'Context/ClassroomContext';
import { useLearnCraftVideoPlayer } from 'Context/LearnCraftVideoPlayer';
import InformationButton from 'Components/VideoEndingOverlay/Button/InformationButton';
import { SvgIcon } from 'Components/Base';

import { useClearByLearnEd } from '../Contexts/ClearByLearnEdProvider/ClearByLearnEdProvider';
import ClearButtonView from '../Components/ClearButton';
import ClearToolTip from '../Components/ClearToolTip';
import useShowClearByLearnEdTooltip from '../Domain/useShowClearByLearnEdTooltip';
import { useByteArkSnapshot } from '../../../Domain/useByteArkSnapshot';
import { useMindstampSnapshot } from '../../../Domain/useMindstampSnapshot';
import useScreenCapture from '../../../Domain/useScreenCapture';
import useSubsectionContent from '../../../Domain/useSubsectionContent';

const ClearButtonVideoEndingOverlayView = ({
  onOpenClearModal,
  onClickVideoEndingOverlayContent,
}) => {
  const handleInformationContent = () => {
    onOpenClearModal();
    onClickVideoEndingOverlayContent();
  };

  return (
    <InformationButton
      title="สงสัยเนื้อหาไม่เข้าใจ"
      icon={SvgIcon.ICON_LIST.HELP}
      onClick={handleInformationContent}
      mb={3}
    />
  );
};

ClearButtonVideoEndingOverlayView.propTypes = {
  onOpenClearModal: PropTypes.func.isRequired,
  onClickVideoEndingOverlayContent: PropTypes.func,
};

const ClearButton = ({
  onClickVideoEndingOverlayContent = () => {},
  isVideoEndingOverlayButton = false,
}) => {
  const { classroomCourseId, currentSubsectionId, currentSubsectionReferId } =
    useClassroomContext();
  const [, , { goToFillingFormStep }, , { enableScreenshot }] = useClearByLearnEd();
  const [showTooltip, setShownTooltip] = useShowClearByLearnEdTooltip();
  const { capture } = useByteArkSnapshot();
  const { capture: mindstampSnapshot } = useMindstampSnapshot();
  const { capture: learnCraftCapture } = useLearnCraftVideoPlayer();
  const [, screenCapture] = useScreenCapture();

  const onCapture = useCallback(
    (screenshot) => {
      goToFillingFormStep({ screenshot: screenshot }, { onGoToFillingFormStep: setShownTooltip });
    },
    [goToFillingFormStep, setShownTooltip]
  );

  const { data: subsectionContentData } = useSubsectionContent({
    variables: {
      classroomCourseId,
      subSectionId: currentSubsectionId,
      subSectionReferId: currentSubsectionReferId,
    },
    fetchPolicy: 'cache-only',
  });

  const goToClearTicketForm = async () => {
    const contentProvider =
      subsectionContentData?.classroomSubSectionContent?.assets?.contentAssets?.[0]?.content
        ?.provider;

    if (contentProvider === 'BYTEARK' || (contentProvider === 'BYTEARK' && isPlatform('ios'))) {
      const screenshot = await capture();
      onCapture(screenshot);
    } else if (contentProvider === 'MINDSTAMP') {
      const screenshot = await mindstampSnapshot();
      onCapture(screenshot);
    } else if (contentProvider === 'LEARNCRAFT') {
      const screenshot = await learnCraftCapture();
      onCapture(screenshot);
    } else {
      const body = document.body;
      const video = document.querySelector('video');
      const element = video ?? body;
      return await screenCapture(element, { onCapture });
    }
  };

  const goToClearTicketFormWithoutScreenCapture = useCallback(
    () => goToFillingFormStep(null, { onGoToFillingFormStep: setShownTooltip }),
    [goToFillingFormStep, setShownTooltip]
  );

  const handleOpenClearModal = () => {
    if (enableScreenshot) {
      goToClearTicketForm();
    } else {
      goToClearTicketFormWithoutScreenCapture();
    }
  };

  return (
    <ClearToolTip isShow={!!showTooltip} onClick={setShownTooltip}>
      {isVideoEndingOverlayButton ? (
        <ClearButtonVideoEndingOverlayView
          onOpenClearModal={handleOpenClearModal}
          onClickVideoEndingOverlayContent={onClickVideoEndingOverlayContent}
        />
      ) : (
        <ClearButtonView onOpenClearModal={handleOpenClearModal} />
      )}
    </ClearToolTip>
  );
};

ClearButton.propTypes = {
  onClickVideoEndingOverlayContent: PropTypes.func,
  isVideoEndingOverlayButton: PropTypes.bool,
};

export default ClearButton;
